import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatIDR, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer } from "@phosphor-icons/react";

export default function PurchaseDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);

  useEffect(() => {
    api.get(`/purchases/${id}`).then((r) => setP(r.data));
  }, [id]);

  if (!p) return <div className="p-10 text-sm text-gray-500">Memuat...</div>;

  const margin = (p.selling_price || 0) - (p.purchase_price || 0);

  return (
    <div>
      <div className="no-print border-b border-gray-200 px-10 py-6 flex items-center justify-between bg-white">
        <div className="flex items-center gap-4">
          <Link to="/purchases" className="text-xs uppercase tracking-wider text-gray-600 hover:text-black flex items-center gap-1">
            <ArrowLeft size={12} /> Kembali
          </Link>
          <div className="h-4 border-l border-gray-300" />
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-gray-400 font-medium">Pembelian</div>
            <div className="font-display text-2xl font-bold">{p.car?.brand} {p.car?.model}</div>
          </div>
        </div>
        <Button onClick={() => window.print()} data-testid="print-purchase" className="rounded-none bg-white border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white uppercase tracking-wider text-xs h-10">
          <Printer size={14} className="mr-2" /> Cetak Kwitansi
        </Button>
      </div>

      <div className="p-4 md:p-6 lg:p-10 max-w-4xl mx-auto">
        {/* Print header */}
        <div className="print-only mb-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-display text-2xl font-bold">SHOWROOM FINANCE</div>
              <div className="text-xs text-gray-600 mt-1">Kwitansi Pembelian Kendaraan</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-gray-500">No. Bukti</div>
              <div className="font-mono-num font-semibold">BUY-{p.id?.slice(0, 8).toUpperCase()}</div>
              <div className="text-xs text-gray-500 mt-1">{formatDate(p.purchase_date)}</div>
            </div>
          </div>
          <div className="border-t border-black mt-4" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-gray-200 -m-px mb-6">
          <div className="border border-gray-200 p-6">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-3">Data Penjual</div>
            <div className="space-y-2">
              <div><span className="text-xs text-gray-500">Nama:</span> <span className="font-semibold ml-2">{p.supplier_name}</span></div>
              <div><span className="text-xs text-gray-500">Telepon:</span> <span className="font-mono-num ml-2">{p.supplier_phone || "-"}</span></div>
              <div><span className="text-xs text-gray-500">Alamat:</span> <span className="ml-2">{p.supplier_address || "-"}</span></div>
              <div><span className="text-xs text-gray-500">Tanggal:</span> <span className="font-mono-num ml-2">{formatDate(p.purchase_date)}</span></div>
            </div>
          </div>
          <div className="border border-gray-200 p-6">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-3">Data Kendaraan</div>
            <div className="space-y-2">
              <div><span className="text-xs text-gray-500">Merek/Model:</span> <span className="font-semibold ml-2">{p.car?.brand} {p.car?.model}</span></div>
              <div><span className="text-xs text-gray-500">Type:</span> <span className="ml-2">{p.car?.type || "-"}</span></div>
              <div><span className="text-xs text-gray-500">Tahun:</span> <span className="font-mono-num ml-2">{p.car?.year}</span></div>
              <div><span className="text-xs text-gray-500">Warna:</span> <span className="ml-2">{p.car?.color || "-"}</span></div>
              <div><span className="text-xs text-gray-500">No. Polisi:</span> <span className="font-mono-num ml-2">{p.car?.plate_number || "-"}</span></div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-0 border border-gray-200 -m-px mb-6">
          <div className="border border-gray-200 p-6">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Harga Beli</div>
            <div className="mt-2 font-mono-num font-semibold text-xl">{formatIDR(p.purchase_price)}</div>
          </div>
          <div className="border border-gray-200 p-6">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Rencana Jual</div>
            <div className="mt-2 font-mono-num font-semibold text-xl">{formatIDR(p.selling_price)}</div>
          </div>
          <div className="border border-gray-200 p-6" style={{ backgroundColor: margin >= 0 ? "#F0FDF4" : "#FEF2F2" }}>
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Estimasi Margin</div>
            <div className="mt-2 font-mono-num font-semibold text-xl" style={{ color: margin >= 0 ? "#10B981" : "#E63946" }}>
              {formatIDR(margin)}
            </div>
          </div>
        </div>

        {p.notes && (
          <div className="border border-gray-200 p-6 mb-6">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Catatan</div>
            <div className="text-sm">{p.notes}</div>
          </div>
        )}

        {/* Signatures */}
        <div className="mt-12 grid grid-cols-2 gap-16 print:pt-8">
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-16">Penjual,</div>
            <div className="border-t border-gray-400 pt-2 text-sm">
              ({p.supplier_name})
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-16">Pembeli / Showroom,</div>
            <div className="border-t border-gray-400 pt-2 text-sm">
              (____________________)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
